<template>
  <v-card
    width="520"
    rounded="lg"
    flat
    :color="$vuetify.theme.dark ? 'background_normal' : 'background_light'"
    :style="
      $vuetify.breakpoint.mdAndUp
        ? 'padding: 56px 48px'
        : 'padding: 32px 20px'
    "
  >
    <div class="d-flex flex-column" style="gap: 24px">
      <div class="text-center d-flex flex-column" style="gap: 8px">
        <span class="h3 bold onSurface_dark--text">Admin Login</span>
        <span class="title-2 regular onSurface_light--text">
          Sign in with Google or an email link.
        </span>
      </div>

      <v-alert
        v-if="deniedRole"
        dense
        outlined
        type="warning"
        border="left"
      >
        This account does not have admin access.
      </v-alert>

      <v-alert v-if="message" dense outlined type="info" border="left">
        {{ message }}
      </v-alert>

      <v-card
        flat
        outlined
        class="pa-4"
        :color="$vuetify.theme.dark ? 'background_dark' : 'white'"
      >
        <div
          class="d-flex align-center justify-space-between"
          style="gap: 16px"
        >
          <div class="d-flex align-center" style="gap: 16px">
            <v-avatar size="44" color="white">
              <v-img
                max-width="34"
                max-height="34"
                :src="require('@/assets/logos/auth/google.png')"
              />
            </v-avatar>
            <div class="d-flex flex-column">
              <span class="subtitle-1 bold onSurface_dark--text">
                Continue with Google
              </span>
              <span class="caption onSurface_light--text">
                Fastest path for admin access.
              </span>
            </div>
          </div>
          <v-btn
            color="primary"
            depressed
            :loading="isLoading && loadingMode === 'google'"
            :disabled="isGoogleDisabled"
            @click="handleGoogleLogin"
          >
            Sign in
          </v-btn>
        </div>
      </v-card>

      <v-card
        flat
        outlined
        class="pa-4"
        :color="$vuetify.theme.dark ? 'background_dark' : 'white'"
      >
        <div class="d-flex flex-column" style="gap: 16px">
          <div class="d-flex flex-column" style="gap: 6px">
            <span class="subtitle-1 bold onSurface_dark--text">
              Email link sign-in
            </span>
            <span class="caption onSurface_light--text">
              {{ emailLinkMode
                ? "Finish the sign-in from the email you opened."
                : "Use the admin email address connected to your account." }}
            </span>
          </div>

          <v-text-field
            v-model="email"
            type="email"
            hide-details="auto"
            outlined
            dense
            :disabled="isLoading"
            label="Email address"
            @keydown.enter.prevent="handleEmailSubmit"
          />

          <div
            class="d-flex align-center hover-pointer"
            @click="keepSignIn = !keepSignIn"
          >
            <v-icon
              :color="keepSignIn ? 'primary' : 'onSurface_normal'"
              size="18"
            >
              {{
                keepSignIn
                  ? "mdi-checkbox-marked"
                  : "mdi-checkbox-blank-outline"
              }}
            </v-icon>
            <span class="ml-2 subtitle-2 onSurface_dark--text">
              Keep me signed in
            </span>
          </div>

          <v-btn
            outlined
            color="primary"
            :loading="isLoading && loadingMode === 'email'"
            :disabled="isEmailDisabled"
            @click="handleEmailSubmit"
          >
            {{ submitLabel }}
          </v-btn>

          <v-alert
            v-if="hasRequestedEmailLink && sentEmail && !emailLinkMode"
            dense
            text
            type="success"
          >
            A sign-in link was sent to {{ sentEmail }}.
          </v-alert>
        </div>
      </v-card>
    </div>
  </v-card>
</template>

<script>
import API from "../../API/auth";

export default {
  data() {
    return {
      email: "",
      keepSignIn: true,
      message: "",
      isLoading: false,
      loadingMode: "",
      deniedRole: false,
      emailLinkMode: false,
      sentEmail: "",
      hasRequestedEmailLink: false,
    };
  },
  computed: {
    isGoogleDisabled() {
      return this.isLoading || this.emailLinkMode;
    },
    isEmailDisabled() {
      return this.isLoading;
    },
    submitLabel() {
      if (this.isLoading && this.loadingMode === "email") {
        return this.emailLinkMode
          ? "Completing sign-in..."
          : "Sending link...";
      }

      return this.emailLinkMode
        ? "Complete email sign-in"
        : "Send sign-in link";
    },
  },
  mounted() {
    this.deniedRole = this.$route.query.denied === "role";
    this.emailLinkMode = API.isAdminEmailLink();

    const storedState = API.getStoredAdminEmailLinkState();
    if (storedState?.email) {
      this.email = storedState.email;
      this.keepSignIn = storedState.keepSignIn;
    }

    if (this.emailLinkMode && storedState?.email) {
      this.completeEmailLinkLogin({
        email: storedState.email,
        keepSignIn: storedState.keepSignIn,
        silent: true,
      });
    } else if (this.emailLinkMode) {
      this.message =
        "Enter the email used for the link to complete sign-in.";
    }
  },
  methods: {
    moveToAdmin() {
      this.$router.replace({ path: "/admin/orders", query: { type: "NEW" } });
    },
    async denyByRole() {
      await this.$firebase.auth().signOut();
      this.$router.replace({ path: "/login", query: { denied: "role" } });
    },
    async handleGoogleLogin() {
      this.isLoading = true;
      this.loadingMode = "google";
      this.message = "";

      try {
        const result = await API.signInWithGoogle(this.keepSignIn);

        if (!result.isAdmin) {
          await this.denyByRole();
          return;
        }

        this.moveToAdmin();
      } catch (error) {
        this.message =
          error instanceof Error ? error.message : "Google sign-in failed.";
      } finally {
        this.isLoading = false;
        this.loadingMode = "";
      }
    },
    async handleSendEmailLink() {
      const normalizedEmail = String(this.email || "").trim();

      if (!normalizedEmail) {
        this.message = "Enter the email address to receive a sign-in link.";
        return;
      }

      this.isLoading = true;
      this.loadingMode = "email";
      this.message = "";

      try {
        const result = await API.sendAdminSignInLink(
          normalizedEmail,
          this.keepSignIn,
        );
        this.email = result.email;
        this.sentEmail = result.email;
        this.hasRequestedEmailLink = true;
        this.message = "Sign-in link sent. Check your inbox.";
      } catch (error) {
        this.message =
          error instanceof Error
            ? error.message
            : "Failed to send the sign-in link.";
      } finally {
        this.isLoading = false;
        this.loadingMode = "";
      }
    },
    async completeEmailLinkLogin(options = {}) {
      const targetEmail = String(options.email || this.email || "").trim();

      if (!targetEmail) {
        this.message = "Enter the email used for the sign-in link.";
        return;
      }

      this.isLoading = true;
      this.loadingMode = "email";
      this.message = options.silent ? "Checking the sign-in link..." : "";

      try {
        const result = await API.completeAdminEmailLinkSignIn({
          email: targetEmail,
          keepSignIn:
            typeof options.keepSignIn === "boolean"
              ? options.keepSignIn
              : this.keepSignIn,
        });

        this.email = result.email;
        this.sentEmail = result.email;

        if (!result.isAdmin) {
          await this.denyByRole();
          return;
        }

        this.moveToAdmin();
      } catch (error) {
        this.message =
          error instanceof Error
            ? error.message
            : "Email link sign-in failed.";
      } finally {
        this.isLoading = false;
        this.loadingMode = "";
      }
    },
    handleEmailSubmit() {
      if (this.emailLinkMode) {
        this.completeEmailLinkLogin();
        return;
      }

      this.handleSendEmailLink();
    },
  },
};
</script>
